import {
  Autocomplete,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MDButton from "components/MDButton";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateCompanyInput,
  CustomCreateProposalMutationVariables,
  ProposalSource,
} from "generated/graphql";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import { createProposalSchema, getDefaultValues } from "DDD/action-objects/ProposalCreate";
import MDBox from "components/MDBox";
import CompanyOptions from "modules/companies/CompanyOptions";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import MDTypography from "components/MDTypography";
import CreateCompany from "modules/companies/CreateCompany/CreateCompany";
import useCreateCompany from "hooks/companies/useCreateCompany";
import { useCallback, useEffect, useMemo, useState } from "react";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";

const CONFIDENCE_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const CONTROL_HEIGHT = 53;

const TEXTFIELD_PROPS = {
  fullWidth: true as const,
  size: "medium" as const,
  variant: "outlined" as const,
};

const FIELD_SX = {
  "& .MuiInputBase-root": {
    borderRadius: 2,
  },
  "& .MuiOutlinedInput-root": {
    height: CONTROL_HEIGHT,
  },
  "& .MuiOutlinedInput-input": {
    paddingTop: "16.5px",
    paddingBottom: "16.5px",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
  },
  "& .MuiFormHelperText-root": {
    minHeight: 18,
    marginLeft: 0,
  },
};

function InfoAdornment({ title }: { title: string }) {
  return (
    <InputAdornment position="end">
      <Tooltip title={title} arrow>
        <IconButton size="small" tabIndex={-1} aria-label="Field info">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );
}

export default function CreateProposalForm({
  onSubmit,
  companyInitialId,
}: {
  onSubmit: SubmitHandler<CustomCreateProposalMutationVariables>;
  companyInitialId?: string;
}) {
  const { open, onClose, onOpen } = useModalState();
  const [triggerRefetch, setTriggerRefetch] = useState(false);

  const methods = useForm<CustomCreateProposalMutationVariables>({
    resolver: zodResolver(createProposalSchema),
    defaultValues: getDefaultValues({ companyId: companyInitialId }),
  });

  const companyId = methods.watch("companyId");

  const [createCompany, { loading: createCompanyLoading, getData, isSuccess }] = useCreateCompany();

  const onCreateCompanySubmit = useCallback(
    async (input: CreateCompanyInput) => {
      const result = await createCompany({ variables: { input } });
      const success = isSuccess(result.data);
      const data = getData(result);

      if (success) {
        onClose();
        setTriggerRefetch(true);
      }

      return { success, data };
    },
    [createCompany, getData, isSuccess, onClose]
  );

  const [getCompany, { company }] = useGetCompanyLazy();

  useEffect(() => {
    if (companyId) getCompany({ id: companyId });
  }, [getCompany, companyId]);

  const proposalSourceOptions = useMemo(() => enumToValueOptions(ProposalSource) ?? [], []);

  return (
    <>
      <FormProvider {...methods}>
        <MDBox
          p={2}
          component="form"
          role="form"
          onSubmit={methods.handleSubmit(async (values) => {
            const result = await onSubmit(values);
            // @ts-expect-error: FIX update types
            if (result.success) {
              methods.reset();
            }
          })}
        >
          <Grid container spacing={2}>
            <CompanyOptions
              triggerRefetch={triggerRefetch}
              selectedCompany={company}
              setTriggerRefetch={(trigger) => setTriggerRefetch(trigger)}
            >
              {({ companyOptions, companyContactOptions, loading, search, pagination }) => {
                const contactOptions = companyId ? companyContactOptions[companyId] ?? [] : [];

                return (
                  <>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="companyId"
                        control={methods.control}
                        render={({ field }) => (
                          <ScrollAsyncAutocomplete
                            {...field}
                            value={companyOptions?.find((x) => x.value === field.value)}
                            options={companyOptions ?? []}
                            loading={loading}
                            label="Select Client"
                            search={search}
                            onLoadMore={pagination.loadMore}
                            hasMore={pagination.hasNextPage}
                            error={methods.formState.errors?.companyId?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <MDBox display="flex" gap={1.5} alignItems="flex-start">
                        <MDBox flex={1}>
                          <Controller
                            name="contactId"
                            control={methods.control}
                            render={({ field, fieldState }) => {
                              const valueObj =
                                contactOptions.find((o: any) => o?.value === field.value) ?? null;

                              return (
                                <Autocomplete
                                  options={contactOptions as any[]}
                                  value={valueObj}
                                  loading={loading}
                                  disabled={!companyId || loading}
                                  isOptionEqualToValue={(opt: any, val: any) =>
                                    opt?.value === val?.value
                                  }
                                  getOptionLabel={(opt: any) => opt?.label ?? ""}
                                  onChange={(_, option: any) =>
                                    field.onChange(option?.value ?? null)
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      {...TEXTFIELD_PROPS}
                                      label={`Select Contact ${companyId ? "" : "(Select Client First)"}`}
                                      error={!!fieldState.error}
                                      helperText={fieldState.error?.message || " "}
                                      sx={{
                                        ...FIELD_SX,
                                        "& .MuiOutlinedInput-root": {
                                          height: CONTROL_HEIGHT,
                                          paddingRight: "39px",
                                        },
                                        "& .MuiAutocomplete-input": {
                                          paddingTop: "16.5px !important",
                                          paddingBottom: "16.5px !important",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              );
                            }}
                          />
                        </MDBox>

                        <MDButton
                          type="button"
                          size="small"
                          variant="gradient"
                          color="secondary"
                          onClick={onOpen}
                          sx={{
                            minWidth: 140,
                          }}
                        >
                          Create Client
                        </MDButton>
                      </MDBox>
                    </Grid>

                  </>
                );
              }}
            </CompanyOptions>
            <Grid item xs={12} md={4}>
              <Controller
                name="source"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...TEXTFIELD_PROPS}
                    select
                    label="Proposal Source"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || " "}
                    sx={FIELD_SX}
                  >
                    <MenuItem value="">None</MenuItem>
                    {proposalSourceOptions.map((opt: any) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="projectedCloseDate"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...TEXTFIELD_PROPS}
                    type="date"
                    label="Projected Close Date"
                    InputLabelProps={{ shrink: true }}
                    value={typeof field.value === "string" ? field.value : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || " "}
                    sx={FIELD_SX}
                    InputProps={{
                      endAdornment: (
                        <>
                          <InfoAdornment title="Expected accept/reject date (not job start)" />
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="closeConfidence"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...TEXTFIELD_PROPS}
                    select
                    label="Close Confidence"
                    InputLabelProps={{ shrink: true }}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.value === "" ? null : Number(e.target.value))
                    }
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || " "}
                    sx={FIELD_SX}
                    InputProps={{
                      endAdornment: <InfoAdornment title="Used for weighted forecasting" />,
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {CONFIDENCE_OPTIONS.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}%
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton
                  size="small"
                  variant="gradient"
                  color="info"
                  type="submit"
                  sx={{ minWidth: 140 }}
                >
                  Continue
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </FormProvider>

      <Modal open={open} onClose={onClose} styles={{ overflow: "auto" }}>
        <MDTypography variant="h6" p={3}>
          Create New Contact
        </MDTypography>
        <CreateCompany onSubmit={onCreateCompanySubmit} loading={createCompanyLoading} />
      </Modal>
    </>
  );
}
